import { Storage } from 'aws-amplify';

const storageConfig = () => ({
  expires: process.env.REACT_APP_AMPLIFY_STORAGE_GET_EXPIRES,
  level: 'public',
  download: false,
});

// A DQF can reach a viewer with no document ever attached (cancelled files, files still
// being assembled). That is not a transient failure, so callers must not offer a retry:
// the object will never appear. Distinguished from every other failure by a 404.
export const isPdfMissing = error =>
  error?.status === 404 || error?.$metadata?.httpStatusCode === 404;

// The raw presigned S3 URL. Never hand this to an <iframe> — see fetchPdfObjectUrl below.
export const getPdfSignedUrl = key => Storage.get(key, storageConfig());

// The URL for the mobile "View Document" anchor, which the user opens in their own tab.
// A blob: URL is wrong there — it is revoked when this screen unmounts and downloads
// without a filename — so the anchor gets the presigned URL.
//
// It is fetched first and the URL returned unchanged. That is deliberate, and matches
// what shipped before CP-853: handing the anchor a presigned URL that has NOT been
// fetched leaves the link failing when it is followed, while fetching it first makes the
// link work. Dropping this step was a regression introduced during CP-853 and caught in
// testing. The fetch also gives us the status, so a missing document is still told apart
// from a failure without a separate existence check.
export const fetchPdfLinkUrl = async key => {
  const signedUrl = await getPdfSignedUrl(key);
  const response = await fetch(signedUrl);

  if (!response.ok) {
    const error = new Error(`PDF request for ${key} failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return signedUrl;
};

// CP-853: Download the PDF once and hand the embedded viewer an in-memory blob: URL.
//
// The previous approach fetched the presigned URL to check it was reachable, threw the
// bytes away, and pointed an <iframe> at the same URL — so S3 served the object a second
// time. Only the first request was ever validated, and when the second one was rejected
// the browser's PDF viewer rendered S3's XML error body as plain text to the user.
//
// Fetching to a blob removes the second request entirely: if the download succeeds the
// document renders, and if it fails the caller gets an exception it can surface.
//
// The returned object URL must be released with URL.revokeObjectURL once the viewer is
// done with it, otherwise the PDF stays in memory for the lifetime of the document.
export const fetchPdfObjectUrl = async key => {
  const signedUrl = await getPdfSignedUrl(key);
  const response = await fetch(signedUrl);

  if (!response.ok) {
    const error = new Error(`PDF request for ${key} failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};
